import { ProductItem } from '@/features/common/components/Overview/parts/ProductItem/types'
import { PriceItem } from '@/features/common/components/Overview/types'
import BottomSheetComponent from '@/features/pos/components/OrderFinalization/parts/BottomSheet/Component'
import { usePortal } from '@/features/pos/components/OrderFinalization/parts/BottomSheet/contexts/Portal'
import useBottomSheetKey from '@/features/pos/components/OrderFinalization/parts/BottomSheet/hooks/useBottomSheetKey'
import { useCancelOrderConfirm } from '@/features/pos/components/OrderFinalization/parts/BottomSheet/hooks/useCancelOrderConfirm'
import { usePrepareOrder } from '@/features/pos/components/OrderFinalization/parts/BottomSheet/hooks/usePrepareOrder'
import { useSelectPosPaymentType } from '@/features/pos/components/OrderFinalization/parts/BottomSheet/hooks/useSelectPosPaymentType'
import CancelOrderConfirmationModal from '@/features/pos/components/OrderFinalization/parts/BottomSheet/parts/CancelOrderConfirmationModal'
import { useIsOpen } from '@/features/pos/components/OrderFinalization/parts/BottomSheet/parts/CancelOrderConfirmationModal/hooks/useIsOpen'
import { PaymentType } from '@/features/pos/components/OrderFinalization/parts/BottomSheet/types'
import { useNoteModal } from '@/features/pos/components/OrderFinalization/parts/FormFields/parts/NoteField/hooks/useNoteModal'
import { navigateToOrderFinalization } from '@/features/pos/components/OrderFinalization/utils/navigateToOrderFinalization'
import { usePosMode } from '@/features/pos/contexts/PosMode'
import { useScreenLayout } from '@/hooks/screen'
import { FC, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

interface BottomSheetProps {
    cartEmpty: boolean
    cartItems: ProductItem[]
    price?: string
    priceItems?: PriceItem[]
    paymentTypes: PaymentType[]
    shouldAttachAutomaticContainers: boolean
    actionDisabled: boolean
}

const BottomSheet: FC<BottomSheetProps> = ({
    cartEmpty,
    cartItems,
    price,
    priceItems,
    paymentTypes,
    shouldAttachAutomaticContainers,
    actionDisabled,
}) => {
    const { t } = useTranslation()
    const key = useBottomSheetKey()

    const mode = usePosMode()

    const layout = useScreenLayout()

    const [isClearOrderConfirmationModalOpen, { open, close }] = useIsOpen()

    const handlePosPaymentTypeSelect = useSelectPosPaymentType(shouldAttachAutomaticContainers)

    const handlePrepare = usePrepareOrder(shouldAttachAutomaticContainers)

    const handleCancelOrderConfirm = useCancelOrderConfirm(close)

    const showNoteModal = useNoteModal()

    const MemoizedBottomSheet = useMemo(
        () => (
            <>
                <BottomSheetComponent
                    key={key}
                    layout={layout}
                    cartEmpty={cartEmpty}
                    cartItems={cartItems}
                    price={price}
                    priceItems={priceItems}
                    paymentTypes={paymentTypes}
                    actionDisabled={actionDisabled}
                    actionLabel={
                        mode === 'pos'
                            ? t('pos.orderFinalization.actions.pay')
                            : t('pos.orderFinalization.actions.prepare')
                    }
                    onCancel={open}
                    onContinue={() => navigateToOrderFinalization(mode)}
                    onInsertNote={showNoteModal}
                    onAction={mode === 'pos' ? handlePosPaymentTypeSelect : handlePrepare}
                />
                <CancelOrderConfirmationModal
                    open={isClearOrderConfirmationModalOpen}
                    onConfirm={handleCancelOrderConfirm}
                    onClose={close}
                />
            </>
        ),
        [
            key,
            mode,
            layout,
            cartEmpty,
            cartItems,
            price,
            priceItems,
            paymentTypes,
            actionDisabled,
            isClearOrderConfirmationModalOpen,
            open,
            handleCancelOrderConfirm,
            close,
            handlePosPaymentTypeSelect,
            handlePrepare,
            showNoteModal,
            t,
        ]
    )

    usePortal(true, MemoizedBottomSheet)

    if (layout === 'extended') return MemoizedBottomSheet
}

export default BottomSheet
